import { parseOrigin } from '@foody/common'
import {
  AppCountry,
  AppEnvironment,
  AppFoodServer,
  AppFoodServerGolang,
  AppFoodServerPython,
  AppOpenApiServer,
  AppToken,
  TrackPlatform,
} from '@/constants/app.constant'
import {
  Country,
  Environment,
  OpenApiServerEnvironmentMapping,
  ServerCountryMapping,
  ServerEnvironmentMapping,
  ServerEnvironmentMappingVn,
} from '@/constants/env.constant'
import { delCookie, getCookie, setCookies } from './utils/cookie.util'
import { COOKIE_CACHE_KEYS } from './constants/cache.constant'
import { initLanguage } from './utils/i18n'
import { initLocaleConfig, localeConfig } from './locale'
import { SDKConfig, Service, bootstrap as ShareSDKBootstrap } from '@mss/partner-sdk'
import { initMomentLocale } from './utils/initMoment'

export async function initApp(
  user: NonNullable<NonNullable<Window['bridgeData']['data']>['user']>,
) {
  initAppEnv()
  initRegion(user.region as Country)

  initLanguage(user.language as any)

  initTracker()

  initBaseUrl()
  await removeCookies()
  await initPFB()

  if (user.shopeeToken) {
    await initToken(user.shopeeToken)
  }

  initLocaleConfig()

  initMomentLocale(user.language as any)

  await initPartnerSdk(user)
}

export function initRegion(region?: Country) {
  const finalRegion = region || (process.env.APP_REGION as Country) || Country.id

  AppCountry.current = finalRegion
}

function initAppEnv() {
  let finalEnv: string | undefined
  if (isLocalDevelopment()) {
    finalEnv = process.env.APP_ENV as Environment
  } else {
    const value = parseOrigin()
    finalEnv = value.env as Environment
  }

  AppEnvironment.current = (finalEnv as unknown as Environment) || Environment.Live
}

function removeCookies() {
  // 登陆 live 环境后，再登陆其他环境，cookie 中会存在 live 环境相关的，导致接口请求时鉴权失败，因此非 live 环境需要在进入页面后先移除无用 cookie
  // cookie 数据是 golang 服务用到的，这里需要用 golang 服务的 domain
  return new Promise((resolve) => {
    if (!AppEnvironment.isLive) {
      const liveDomain = AppFoodServerGolang.current.split('.').slice(2).join('.')
      const cookieNames = [
        COOKIE_CACHE_KEYS.requestToken,
        COOKIE_CACHE_KEYS.requestEntityId,
        COOKIE_CACHE_KEYS.requestMerchantId,
        COOKIE_CACHE_KEYS.requestFrom,
      ]
      cookieNames.forEach((name) => {
        delCookie(name, '/', liveDomain)
      })
    }

    resolve(true)
  })
}

function initPFB() {
  return new Promise((resolve) => {
    // FIXME: PFB工具设置的pfb domain使用当前网页域名，无法带到foody接口上的问题
    // 有成熟的解决方案以后可以去掉本段逻辑
    if (!AppEnvironment.isLive) {
      const pfb = getCookie('_SPC_PFB') || getCookie('_MSS_PFB')
      if (pfb) {
        setCookies([
          {
            name: '_SPC_PFB',
            value: pfb,
          },
        ])
      }
    }

    resolve(true)
  })
}

enum RequestFromType {
  PartnerWeb = 'partner_web',
  PartnerApp = 'partner_app',
}

function initToken(token: string) {
  AppToken.current = token

  return new Promise((resolve) => {
    setCookies([
      {
        name: COOKIE_CACHE_KEYS.requestToken,
        value: token,
      },
      {
        name: COOKIE_CACHE_KEYS.requestFrom,
        value: RequestFromType.PartnerWeb,
      },
    ])

    resolve(true)
  })
}

function initBaseUrl() {
  AppFoodServer.current = getFoodyServer()
  AppOpenApiServer.current = getOpenApiServer()
}

export function getFoodyServerGolang(env: Environment, country: Country) {
  return `https://${ServerEnvironmentMapping[env]}${ServerCountryMapping[country]}`
}

function getFoodyServerPython(env: Environment) {
  return `https://${ServerEnvironmentMappingVn[env]}`
}

function getFoodyServer(env = AppEnvironment.current, country = AppCountry.current) {
  AppFoodServerGolang.current = getFoodyServerGolang(env, country)
  AppFoodServerPython.current = getFoodyServerPython(env)

  if (isLocalDevelopment()) {
    // 让only_dev走webpack-dev-server的proxy
    return window.location.origin
  }

  return AppCountry.isVn ? AppFoodServerPython.current : AppFoodServerGolang.current
}

function getOpenApiServer(env = AppEnvironment.current, country = AppCountry.current) {
  if (process.env.NODE_ENV === 'development' && !(window as any).__POWERED_BY_QIANKUN__) {
    // 让only_dev走webpack-dev-server的proxy
    return window.location.origin
  }

  return `https://${OpenApiServerEnvironmentMapping[env]}${ServerCountryMapping[country]}`
}

function isLocalDevelopment() {
  return process.env.NODE_ENV === 'development' && !(window as any).__POWERED_BY_QIANKUN__
}

async function initPartnerSdk(
  user: NonNullable<NonNullable<Window['bridgeData']['data']>['user']>,
) {
  ShareSDKBootstrap({
    APP_PORTAIL: 'partner_web',
    APP_SERVICE: Service.shopeefood,
    APP_ENV: process.env.APP_ENV as Environment,
    APP_LANGUAGE: user.language as SDKConfig['APP_LANGUAGE'],
    APP_REGION: user.region as SDKConfig['APP_REGION'],
    APP_TOKEN: user.shopeeToken,
    APP_PFB: '',
    APP_TIMEZONE: localeConfig.defaultTimezone,
    APP_USER_ID: user.userId + '',
  })
}

function initTracker() {
  if (typeof window.trackingSDK?.ubt === 'function') return

  // https://confluence.shopee.io/display/SDE/%5BOps%5D+TMS+Debug+View+%28Web%29+Integration+Guide
  window.trackingSDK.init(
    ['ubt', 'debugview'],
    (name?: string) => {
      if (name === 'debugview') {
        return {
          appId: 12,
          platform: TrackPlatform,
          locale: process.env.APP_REGION || 'id',
          env: process.env.APP_ENV === 'live' ? 'live' : 'test',
          device_id: localStorage.getItem('de_device_id') || Math.random().toString(36).slice(2), // debugview 要求必须有 device_id
          debugMode: true,
          debugEnv: process.env.APP_ENV === 'live' ? 'live' : 'test',
        } as any
      }

      return {
        platform: TrackPlatform,
        appId: 12, // appId 为本项目在 tms 系统上的编号
        env: process.env.APP_ENV || 'test',
        locale: process.env.APP_REGION || 'id',
        enableAutoTrack: false,
      } as any
    },
    '1.1.24',
  )

  // eslint-disable-next-line no-lone-blocks
  {
    ;(window.trackingSDK.ubt as Function)(
      'getUserId' as const,
      window.bridgeData?.data?.user?.userId ?? 0,
    )
  }
}
